import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext({});

export const useFavorites = () => useContext(FavoritesContext);

export const FavoritesProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchFavorites = useCallback(async () => {
    if (!currentUser) {
      setFavorites([]);
      return;
    }
    try {
      setLoading(true);
      const response = await api.get('/auth/favorites');
      setFavorites(response.data.favorites || []);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const isFavorite = (productId) => favorites.includes(productId);

  const toggleFavorite = async (productId) => {
    if (!currentUser) return false;

    const wasFavorite = isFavorite(productId);

    setFavorites(prev =>
      wasFavorite ? prev.filter(id => id !== productId) : [...prev, productId]
    );

    try {
      if (wasFavorite) {
        await api.delete(`/auth/favorites/${productId}`);
      } else {
        await api.post(`/auth/favorites/${productId}`);
      }
      return !wasFavorite;
    } catch (error) {
      setFavorites(prev =>
        wasFavorite ? [...prev, productId] : prev.filter(id => id !== productId)
      );
      console.error('Failed to toggle favorite:', error);
      return wasFavorite;
    }
  };

  const value = {
    favorites,
    loading,
    isFavorite,
    toggleFavorite,
    fetchFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
